import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BalanceRow, BuyButton, HeaderRow, HeaderSpan, OnrampWrapper, CenteredText, Network } from './Onramp.styled'
import { CurrencySelect } from './CurrencySelect/CurrencySelect'
import { DepositInfo } from '../DepositInfo/DepositInfo'
import { useModalHook } from '@src/components/wallet/WalletModal.hook'
import { fetchQuery } from 'relay-runtime'
import { DepositViewComponentDepositAddressQuery } from '@src/components/wallet/__generated__/DepositViewComponentDepositAddressQuery.graphql'
import { getDepositAddressQuery, getPermanentDepositAddressMutation } from '@src/components/wallet/DepositView'
import { Currency } from '../../rightSide/walletComponent/__generated__/WalletComponentlastDepositAddressQuery.graphql'
import Env from '../../../RelayEnvironment'
import { OnRampCurrencyList } from './CurrencySelect/CurrencySelect.list'
import { getCoin, getUrl } from './OnRamp.utils'
import { useMutation } from 'react-relay'
import { DepositViewGetPermanentDepositAddressMutation } from '@src/components/wallet/__generated__/DepositViewGetPermanentDepositAddressMutation.graphql'

interface OnrampProps {
  currency?: string
}

export const Onramp: React.FC<OnrampProps> = () => {
  const { t } = useTranslation()

  const onRampCurrencyList = {
    BTC: {
      withNetwork: false,
    },
    LTC: {
      withNetwork: false,
    },
    USDT: {
      withNetwork: true,
    },
    ETH: {
      withNetwork: true,
    },
  }

  const [generatePermanentPaymentAddress] = useMutation<DepositViewGetPermanentDepositAddressMutation>(
    getPermanentDepositAddressMutation
  )
  const {
    setIsBack,
    isListView,
    setIsListView,
    selectedExternalCoin,
    depositAddressData,
    setDepositAddressData,
    paymentOrder,
    tokens,
  } = useModalHook()
  const [selectedCurrency, setSelectedCurrency] = useState('USDT')
  const [selectedNetwork, setNetwork] = useState('USDT')
  const [coin, setCoin] = useState<
    | {
        symbol: string
        crypto: string
        name: string
        logo: string
        min: string
        price: string
        coinId: string
        status: number
        tokens: readonly any[]
      }
    | undefined
  >(getCoin(selectedCurrency, tokens))

  const onNetworkSelect = (tokenId: string, chain: string) => {
    generateAddress(tokenId, chain)
    setNetwork(chain)
  }

  useEffect(() => {
    setDepositAddressData(undefined)
  }, [])

  const openListView = () => {
    setIsListView(true)
    setIsBack(true)
  }

  const onCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency)

    if (onRampCurrencyList[currency].withNetwork) {
      setDepositAddressData(undefined)
      setCoin(getCoin(currency, tokens))
    } else {
      getDepositAddress(currency)
    }

    setIsListView(false)
    setIsBack(false)
  }

  const getDepositAddress = async (currency: string) => {
    try {
      const data = await fetchQuery<DepositViewComponentDepositAddressQuery>(Env, getDepositAddressQuery, {
        currency: currency as Currency,
      }).toPromise()

      setDepositAddressData(data)
    } catch (error) {
      console.error('Error fetching deposit address:', error)
    }
  }

  const generateAddress = async (tokenId: string, chainId: string) => {
    try {
      generatePermanentPaymentAddress({
        variables: {
          input: {
            tokenId: tokenId,
            chainId: chainId,
          },
        },
        onCompleted: (response) => {
          console.log('response :>> ', response)
          if (response?.getPermanentDepositAddress) {
            const { payAddress } = response.getPermanentDepositAddress

            setDepositAddressData({
              lastDepositAddress: {
                address: payAddress,
                currency: undefined as any,
                isShared: false,
                expireTime: null,
              },
            })
          }
        },
        onError: (error) => {
          console.error('Error in mutation:', error)
        },
      })
    } catch (error) {
      console.error('Error generating permanent address:', error)
    }
  }

  if (isListView) {
    return (
      <OnrampWrapper>
        <OnRampCurrencyList currencyList={onRampCurrencyList} onSelect={onCurrencyChange} />
      </OnrampWrapper>
    )
  }

  return (
    <>
      <OnrampWrapper>
        <>
          <HeaderRow>
            <HeaderSpan>{t('wallet.onramp.wantBuy', 'I want to buy')}</HeaderSpan>
          </HeaderRow>
          <BalanceRow>
            <CurrencySelect currency={selectedCurrency} openListView={openListView} />
          </BalanceRow>
          <Network> {onRampCurrencyList[selectedCurrency].withNetwork && 'With network'}</Network>
          <DepositInfo
            isExternalCurrency={onRampCurrencyList[selectedCurrency].withNetwork}
            selectedExternalCoin={selectedExternalCoin}
            depositAddressData={depositAddressData}
            coin={coin}
            paymentOrder={paymentOrder}
            onNetworkSelect={onNetworkSelect}
          />
          <CenteredText>
            {t(
              'wallet.onramp.redirectNotice',
              'You will be redirected to an external payment service. Please ensure the deposit address matches the one displayed here before completing your transaction.'
            )}
          </CenteredText>
        </>

        {/* Copy wallet adress */}
        <BuyButton href={getUrl({ network: selectedNetwork, currency: selectedCurrency })} target="_blank">
          {t('wallet.onramp.buyCrypto', 'Buy crypto')}
        </BuyButton>
      </OnrampWrapper>
    </>
  )
}
